import { ITripOfferModel } from "./ITrip";

export interface IFmlmView {
  id: string;
  legs: IFmlmLeg[];
  arrivalTimestampPlanned: number;
  arrivalTimezone: string;
  departureTimestampPlanned: number;
  arrivalPoi: Poi;
  departurePoi: Poi;
  departureTimezone: string;
  frequency: number; 
  tripOffer?: ITripOfferModel;
  intermodal: boolean;
  attributes: {
    SCORE?: number;
    STABLE_JOURNEY_SEGMENT_ID?: string;
    TRACE_ID?: string;
  }
}

type Poi = {
  lon: number;
  lat: number;
  name: string;
  type: string;
};

export interface IFmlmLeg {
  id: string;
  transitMode: "WALKING" | "BUS" | string;
  departurePoi: IFmlmLegPoi;
  departureTimestampPlanned: number;
  arrivalPoi: IFmlmLegPoi;
  arrivalTimestampPlanned: number;
  polyline: string;
  environmentImpact: {
    co2eGrams: number;
  },
  transitDetails?: any;
}

type IFmlmLegPoi = {
  id: string;
  lon: number;
  lat: number;
  name: string;
  sourceId: string;
  sourceName: string;
  type: string;
  stopCode: string;
};

export interface IFmlmJourneyResponseAtom {
  outboundJourneys: IFmlmView[] | null;
  pagingContext: any | null;
}

export interface IFmlmFilter {
  mode: "leave" | "arrive";
  hour: number; // 0-23
  minute: number; // 0-59
  flexHr: 1 | 2; // 1-2
  originTransportMode: TFmlmFilterTransportMode[];
  destinationTransportMode: TFmlmFilterTransportMode[];
  sortBy: TFmlmFilterSortOptions;
}

export interface IFmlmFilterDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const FMLM_FILTER_TRANSPORT_MODES = [
  {
    value: "car-taxi",
    label: "Car/Taxi",
    modes: ["CAR"],
    disabled: false,
    excludeCode: "DRIVING",
    exclusionType: "origin",
  },
  {
    value: "shuttle",
    label: "Shuttle",
    modes: ["SHUTTLE"],
    disabled: false,
    excludeCode: "SHUTTLE",
    exclusionType: "city",
  },
  {
    value: "public-transport",
    label: "Public Transport",
    modes: ["METRO", "BUS"],
    disabled: false,
    excludeCode: "TRANSIT",
    exclusionType: "city",
  },
] as const;
export type TFmlmFilterTransportMode =
  (typeof FMLM_FILTER_TRANSPORT_MODES)[number]["value"];

export const FMLM_FILTER_SORT_OPTIONS = [
  { value: "default", label: "Default", disabled: false },
  { value: "FASTEST", label: "Fastest", disabled: false },
  { value: "FEWEST_TRANSFERS", label: "Less Transfers", disabled: false },
  { value: "ENVIRONMENTAL_IMPACT", label: "Eco Friendly", disabled: false },
] as const;
export type TFmlmFilterSortOptions =
  (typeof FMLM_FILTER_SORT_OPTIONS)[number]["value"];

export const DEFAULT_FMLM_FILTER_STATE: IFmlmFilter = {
  hour: 8,
  minute: 0,
  flexHr: 1,
  mode: "leave",
  originTransportMode: FMLM_FILTER_TRANSPORT_MODES.filter(
    (mode) => !mode.disabled
  ).map((mode) => mode.value),
  destinationTransportMode: FMLM_FILTER_TRANSPORT_MODES.filter(
    (mode) => !mode.disabled
  ).map((mode) => mode.value),
  sortBy: "default",
};

export const hasFmlmFilterChanged = (fmlmFilter: IFmlmFilter) => {
  const compareFields = [
    "originTransportMode",
    "destinationTransportMode",
    "sortBy",
  ];

  const isEqual = (a: any, b: any): boolean => {
    if (typeof a !== typeof b) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return (
        a.every((item) => b.includes(item)) &&
        b.every((item) => a.includes(item))
      );
    }
    if (a && typeof a === "object" && b && typeof b === "object") {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      return aKeys.every((key) => isEqual(a[key], b[key]));
    }
    return a === b;
  };

  return !compareFields.every((field) =>
    isEqual(
      fmlmFilter[field as keyof IFmlmFilter],
      DEFAULT_FMLM_FILTER_STATE[field as keyof IFmlmFilter]
    )
  );
};